import { GameState } from './types';
import { WORLD_W, WORLD_H, AUTO_SAVE_INTERVAL } from './constants';
import { initInput, readInput } from './input';
import { updateCamera } from './camera';
import { render } from './renderer';
import { updatePlayer } from './entities/player';
import { updateTrees } from './entities/tree';
import { updateCombat } from './systems/combat';
import { updateBuildMode } from './systems/buildMode';
import { updateParticles, getAliveParticles, initParticles } from './particles';
import { createNewGame } from './systems/world';
import { saveGame, loadSave, applySave } from './save';
import { showTutorial, initTutorialButtons } from './tutorial';
import { updateHUD, initUI, handleUIInput, isAnyModalOpen, updateUpgradeInfo, setOnNewGame } from './ui';
import { resumeAudio } from './audio';

let canvas: HTMLCanvasElement;
let ctx: CanvasRenderingContext2D;
let state: GameState;
let lastTime = 0;
let canvasW = 768;
let canvasH = 640;

function init(): void {
  canvas = document.getElementById('game-canvas') as HTMLCanvasElement;
  ctx = canvas.getContext('2d')!;

  // Set canvas size
  resizeCanvas();
  window.addEventListener('resize', resizeCanvas);

  // Init subsystems
  initInput();
  initParticles();

  // Create or load game
  state = createNewGame();
  const save = loadSave();
  if (save) {
    applySave(state, save);
  }

  // Init UI
  initUI(state);
  initTutorialButtons(state);
  setOnNewGame(resetGame);

  // Show tutorial if first time
  if (state.tutorialStep >= 1) {
    showTutorial(state);
  }

  // Save on page unload
  window.addEventListener('beforeunload', () => {
    saveGame(state);
  });

  // Resume audio on first interaction
  const resumeOnClick = () => {
    resumeAudio();
    window.removeEventListener('click', resumeOnClick);
    window.removeEventListener('keydown', resumeOnClick);
    window.removeEventListener('touchstart', resumeOnClick);
  };
  window.addEventListener('click', resumeOnClick);
  window.addEventListener('keydown', resumeOnClick);
  window.addEventListener('touchstart', resumeOnClick);

  // Start game loop
  lastTime = performance.now();
  requestAnimationFrame(gameLoop);
}

function resizeCanvas(): void {
  // Scale canvas to fit window while maintaining aspect ratio
  const dpr = window.devicePixelRatio || 1;
  const maxW = window.innerWidth;
  const maxH = window.innerHeight - 40; // Account for HUD

  const scaleX = maxW / WORLD_W;
  const scaleY = maxH / WORLD_H;
  const scale = Math.min(scaleX, scaleY, 2); // Cap at 2x

  canvasW = Math.floor(WORLD_W * Math.min(scaleX, 2));
  canvasH = Math.floor(WORLD_H * Math.min(scaleY, 2));

  // Actual display size
  canvas.style.width = `${canvasW}px`;
  canvas.style.height = `${canvasH}px`;

  // Internal resolution (DPI-aware)
  canvas.width = Math.floor(canvasW * dpr);
  canvas.height = Math.floor(canvasH * dpr);
  ctx = canvas.getContext('2d')!;
  ctx.scale(dpr, dpr);

  // Disable image smoothing for pixel-perfect rendering
  ctx.imageSmoothingEnabled = false;
}

function gameLoop(now: number): void {
  const dt = Math.min((now - lastTime) / 1000, 0.1); // Cap delta to avoid spiral of death
  lastTime = now;

  update(dt);
  renderFrame();

  requestAnimationFrame(gameLoop);
}

function update(dt: number): void {
  const input = readInput();

  // Handle UI input first (modals, pause, etc.)
  handleUIInput(state, input);

  // Don't update game while paused or modal open
  if (state.paused || isAnyModalOpen()) return;

  state.totalTime += dt;

  // Player
  if (!state.buildMode || !state.buildSelection) {
    updatePlayer(state, input, dt);
  } else {
    // In build mode with selection - still allow movement but chop places building
    const { chop, ...moveInput } = { ...input, chop: false };
    updatePlayer(state, { ...moveInput, chop: false, throw: false, interact: false, buildToggle: false, craft: false, pause: false, confirm: false }, dt);
  }

  // Trees
  updateTrees(state, dt);

  // Build mode
  updateBuildMode(state, input);

  // Combat (intruder spawning, AI, projectile collisions)
  updateCombat(state, dt);

  // Particles
  const alive = updateParticles(dt);
  state.particles = alive;

  // Camera
  updateCamera(state, canvasW, canvasH);

  // HUD
  updateHUD(state);

  // Auto-save
  state.autoSaveTimer -= dt;
  if (state.autoSaveTimer <= 0) {
    saveGame(state);
    state.autoSaveTimer = AUTO_SAVE_INTERVAL;
  }
}

function renderFrame(): void {
  state.particles = getAliveParticles();
  render(ctx, state, canvasW, canvasH);
}

function resetGame(): void {
  state = createNewGame();
  initUI(state);
  initTutorialButtons(state);
  setOnNewGame(resetGame);
  showTutorial(state);
  updateHUD(state);
}

// Boot
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
